/**
 * ScanQR — dedicated scanner page for the PRESENCE native app (iOS/Android).
 *
 * Used by hospitality volunteers at the Fêtes 2027 entrance:
 *   1. Open the app → tap "Scanner"
 *   2. ML Kit opens the native camera view
 *   3. On a valid QR pointing at /checkin/{token} we navigate to that page
 *   4. On the web, we fall back to a manual paste-token input
 *
 * Not exposed in the public menu — reachable via /scan and via the admin
 * "check-in" shortcut on the Fêtes 2027 dashboard.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isNative, scanQRCode } from '@/lib/native';
import { useI18n } from '@/i18n/I18nContext';
import Ornament from '@/components/Ornament';

function extractCheckinToken(scanned) {
  if (!scanned) return null;
  // If it's a full URL like https://prieure-saint-leonard.fr/checkin/xxx
  try {
    const u = new URL(scanned);
    const m = u.pathname.match(/^\/checkin\/([\w.-]+)$/);
    if (m) return m[1];
  } catch {
    // Not a URL — assume it's a raw token
    if (/^[\w.-]+$/.test(scanned)) return scanned;
  }
  return null;
}

export default function ScanQR() {
  const navigate = useNavigate();
  const { locale } = useI18n();
  const isFr = locale === 'fr';
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!isNative()) return;
    let cancelled = false;
    (async () => {
      setScanning(true);
      try {
        const scanned = await scanQRCode();
        if (cancelled) return;
        const token = extractCheckinToken(scanned);
        if (token) {
          navigate(`/checkin/${token}`, { replace: true });
        } else {
          setError(
            isFr
              ? 'QR code non reconnu. Saisissez le jeton manuellement.'
              : 'Unrecognised QR code. Enter the token manually.'
          );
        }
      } catch (e) {
        setError(
          isFr
            ? "Impossible d'ouvrir la caméra. Saisissez le jeton manuellement."
            : 'Unable to open the camera. Enter the token manually.'
        );
      } finally {
        setScanning(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submitManual = (e) => {
    e.preventDefault();
    const token = extractCheckinToken(manual.trim());
    if (token) navigate(`/checkin/${token}`, { replace: true });
    else
      setError(
        isFr
          ? 'Jeton invalide — collez le lien complet ou l\'identifiant.'
          : 'Invalid token — paste the full link or the identifier.'
      );
  };

  return (
    <div
      data-testid="scan-page"
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{ background: 'var(--parchment)' }}
    >
      <div className="max-w-md w-full text-center">
        <Ornament />
        <h1
          className="font-heading text-3xl mt-6 mb-4"
          style={{ color: 'var(--ink)' }}
        >
          {isFr ? 'Scanner un billet' : 'Scan a ticket'}
        </h1>

        {scanning && (
          <p
            className="font-body italic"
            style={{ color: 'var(--muted)' }}
            data-testid="scan-status"
          >
            {isFr
              ? 'Caméra en cours d\'ouverture…'
              : 'Opening the camera…'}
          </p>
        )}

        {error && (
          <p
            className="font-body my-4"
            style={{ color: '#7b1f2b' }}
            data-testid="scan-error"
          >
            {error}
          </p>
        )}

        <form onSubmit={submitManual} className="mt-8 space-y-3">
          <label
            className="block font-body text-sm text-left"
            style={{ color: 'var(--muted)' }}
          >
            {isFr
              ? 'Ou collez le lien / le jeton :'
              : 'Or paste the link / token:'}
          </label>
          <input
            data-testid="scan-manual-input"
            className="w-full px-4 py-3 border font-body"
            style={{
              background: '#fff',
              borderColor: 'var(--border)',
              color: 'var(--ink)',
            }}
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="https://…/checkin/xxx"
          />
          <button
            data-testid="scan-manual-submit"
            type="submit"
            className="w-full px-6 py-3 font-body tracking-widest uppercase text-sm"
            style={{
              background: 'var(--cinnabar, #7b1f2b)',
              color: '#fff',
            }}
          >
            {isFr ? 'Valider' : 'Confirm'}
          </button>
        </form>
      </div>
    </div>
  );
}
